import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 6 geometry and statistics.
 *
 * The unifying idea in the geometry lessons is that nothing new is being
 * learned — every shape is cut and rearranged back into the rectangle the
 * student already understands, and volume is area with one more dimension.
 */
export const GEOMETRY_MIDDLE_LESSONS: Lesson[] = [
  lesson({
    id: 'coordinate-plane-01',
    unitId: 'unit-coordinate-geometry',
    conceptId: 'coordinate-plane',
    title: 'The Coordinate Plane',
    summary: 'Two number lines crossed at zero, so that every point on a flat surface has a name.',
    objectives: [
      'Plot and read points as ordered pairs',
      'Name the four quadrants and the signs in each',
      'Find the distance between points on the same line',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Naming a place',
        blocks: [
          text('A number line names any point on a line with one number. To name a point on a flat surface you need two — how far across, and how far up.'),
          text('Cross two number lines at their zeros and you have the **coordinate plane**. Every point now has an address, written as an ordered pair.'),
          math('(3, 2)'),
        ],
      },
      intuition: {
        title: 'Along the corridor, then up the stairs',
        blocks: [
          text('The first number is always the horizontal one — how far right (or left, if negative) from the origin. The second is always vertical.'),
          warning('(3, 2) and (2, 3) are different points. Order is the whole point of the phrase "ordered pair", and swapping them is the commonest mistake here.', 'The trap'),
          insight('The old rhyme is worth keeping: **along the corridor, then up the stairs**. Across first, up second, every time.'),
          text('Because negatives are allowed, the plane divides into four regions called quadrants, numbered anticlockwise from the top right.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: -5,
              max: 5,
              showQuadrantLabels: true,
              points: [
                { x: 3, y: 2, label: '(3, 2)' },
                { x: -4, y: 1, label: '(−4, 1)' },
                { x: -2, y: -3, label: '(−2, −3)' },
              ],
            },
            'The signs of the pair tell you the quadrant before you plot anything.',
          ),
          table(['Quadrant', 'x', 'y'], [['I', 'positive', 'positive'], ['II', 'negative', 'positive'], ['III', 'negative', 'negative'], ['IV', 'positive', 'negative']]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('How far apart are the points (−3, 4) and (5, 4)?'),
          steps([
            { label: 'Notice the y-coordinates match, so both points sit on the same horizontal line.', latex: 'y = 4' },
            { label: 'The distance is therefore just the gap between the x-coordinates.', latex: '\\text{from } -3 \\text{ to } 5' },
            { label: 'Count along the line, through zero.', latex: '3 + 5 = 8' },
            { label: 'Answer.', latex: '8 \\text{ units}' },
          ]),
          tip('When two points share a coordinate, distance is a one-dimensional problem again — no new technique needed.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('coordinate-plane', 4)] },
      independent: { title: 'On your own', blocks: [practice('coordinate-plane', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('coordinate-plane', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A point is named by an ordered pair (x, y).',
            'Across first, then up.',
            'The signs of the pair determine the quadrant.',
            'Points sharing a coordinate lie on a line, so their distance is a simple count.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'area-triangles-01',
    unitId: 'unit-area-volume',
    conceptId: 'area-triangles',
    title: 'Area of Triangles and Parallelograms',
    summary: 'Every straight-sided shape can be cut and rearranged into a rectangle you already know how to measure.',
    objectives: [
      'Find the area of a parallelogram',
      'Find the area of a triangle, and explain the halving',
      'Identify the height as perpendicular to the base',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'One formula you already have',
        blocks: [
          text('You know that a rectangle\'s area is length × width. It turns out you do not need a new idea for other straight-sided shapes — only scissors.'),
          math('A_{\\text{parallelogram}} = b \\times h \\qquad A_{\\text{triangle}} = \\tfrac{1}{2} b \\times h'),
        ],
      },
      intuition: {
        title: 'Cut and rearrange',
        blocks: [
          text('Take a parallelogram. Cut the slanted triangle off one end and slide it round to the other. You have a rectangle, with the same base and the same height — and obviously the same area, because nothing was added or removed.'),
          text('Now a triangle. Take a second copy of it, turn it upside down, and push the two together. They form a parallelogram exactly. So one triangle is half of it.'),
          insight('That is where the ½ comes from. It is not an arbitrary factor to memorise — a triangle is literally half of the parallelogram built from two copies of it.'),
          warning('The **height** is the perpendicular distance from the base to the opposite point — straight up, not along a slanted side. On a leaning triangle the height often falls outside the shape entirely.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'shape', shape: 'triangle', labels: { base: 'base = 10 cm', height: 'height = 6 cm' } }, 'The height is the perpendicular drop to the base, never the slanted edge.'),
          figure({ kind: 'grid', width: 10, height: 6 }, 'Two copies of that triangle fill this 10 × 6 rectangle exactly: 60 squares, so one triangle covers 30.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A triangle has a base of 10 cm and a height of 6 cm. Find its area.'),
          steps([
            { label: 'Start with the rectangle around it.', latex: '10 \\times 6 = 60' },
            { label: 'The triangle is half of that.', latex: '\\tfrac{1}{2} \\times 60' },
            { label: 'Answer, in square units.', latex: '30 \\text{ cm}^2' },
          ]),
          warning('Forgetting the ½ gives 60 cm² — the area of the rectangle, not the triangle. If your answer looks like the whole rectangle, you have found the wrong shape.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('area-triangles', 4)] },
      independent: { title: 'On your own', blocks: [practice('area-triangles', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('area-triangles', 3, 'Working back from an area to a missing base or height.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Parallelogram: area = base × height.',
            'Triangle: area = ½ × base × height.',
            'The height is perpendicular to the base, not the slanted side.',
            'Both formulas come from rearranging into a rectangle.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'volume-01',
    unitId: 'unit-area-volume',
    conceptId: 'volume',
    title: 'Volume',
    summary: 'How much space a solid fills — which needs three measurements, not two.',
    objectives: [
      'Find the volume of a rectangular prism',
      'Explain volume as layers of area',
      'Use cubic units correctly',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Filling instead of covering',
        blocks: [
          text('Area answers "how much surface is there to cover?". Volume answers "how much space is there to fill?".'),
          text('Covering a floor needs two measurements. Filling a box needs three, because a box has depth as well as width and length.'),
          math('V = \\ell \\times w \\times h'),
        ],
      },
      intuition: {
        title: 'Stacked layers of area',
        blocks: [
          text('Lay unit cubes across the bottom of a box. That first layer is exactly the area of the base — length × width cubes.'),
          text('Now stack layers until the box is full. If the box is 4 high, you need 4 identical layers.'),
          insight('So volume is base area × height. The three-number formula is just that, written out. Every volume formula you meet later — prisms, cylinders — is the same sentence: area of the base, times how tall.'),
          warning('Volume is measured in **cubic** units: cm³, m³. If your answer has cm² on it, you have found an area and missed a dimension.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'prism', length: 5, width: 3, height: 4, labels: { length: '5 cm', width: '3 cm', height: '4 cm' } },
            'The bottom layer holds 5 × 3 = 15 cubes. Four layers give 60.',
          ),
          figure({ kind: 'grid', width: 5, height: 3 }, 'One layer, seen from above: 15 unit cubes.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A box is 5 cm long, 3 cm wide and 4 cm tall. Find its volume.'),
          steps([
            { label: 'Find the area of the base — one layer of cubes.', latex: '5 \\times 3 = 15' },
            { label: 'Multiply by the height, which is how many layers.', latex: '15 \\times 4 = 60' },
            { label: 'Answer, in cubic units.', latex: '60 \\text{ cm}^3' },
          ]),
          tip('The three numbers can be multiplied in any order — a box on its side holds the same amount.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('volume', 4)] },
      independent: { title: 'On your own', blocks: [practice('volume', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('volume', 3, 'Finding a missing edge from the volume.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Volume = length × width × height.',
            'Equivalently: area of the base × height.',
            'Units are cubed: cm³, m³.',
            'Two measurements give an area, not a volume.',
          ]),
        ],
      },
    },
  }),
];
